// JOBE-0158.4: HeatMap primitive — rows × cols × score cells.
//
// Every cell is hoverable + clickable (D10: every number is a link).
// The `signalCell` prop highlights the "next action" cell in --signal.
// Keyboard navigation: arrow keys between cells, Enter fires onCellClick.

import { useRef, useCallback } from 'react';
import './HeatMap.css';

// Clamp a value into [min, max].
function clamp(v, lo, hi) {
  return Math.max(lo, Math.min(hi, v));
}

// Return the CSS class name for a score value (0–1).
function scoreBand(score) {
  if (score < 0.20) return 'heatmap-cell--ok';
  if (score < 0.50) return 'heatmap-cell--watch';
  if (score < 0.80) return 'heatmap-cell--high';
  return 'heatmap-cell--extreme';
}

// useGridFocus: given a ref to the <table> element, returns a keydown
// handler that implements arrow-key navigation between data cells
// (role="gridcell"). Relies on data-row / data-col attributes set on
// each <td>.
function useGridFocus(tableRef) {
  return useCallback((e) => {
    const current = e.currentTarget;
    const row = parseInt(current.dataset.row, 10);
    const col = parseInt(current.dataset.col, 10);
    const table = tableRef.current;
    if (!table) return;

    let nextRow = row;
    let nextCol = col;
    if (e.key === 'ArrowRight') { nextCol = col + 1; }
    else if (e.key === 'ArrowLeft') { nextCol = col - 1; }
    else if (e.key === 'ArrowDown') { nextRow = row + 1; }
    else if (e.key === 'ArrowUp') { nextRow = row - 1; }
    else { return; }

    e.preventDefault();
    const target = table.querySelector(
      `[data-row="${nextRow}"][data-col="${nextCol}"]`
    );
    if (target) target.focus();
  }, [tableRef]);
}

export function HeatMap({
  rows,
  cols,
  cells,
  rowLabel,
  colLabel,
  onCellClick,
  signalCell,
  ariaLabel,
}) {
  const tableRef = useRef(null);
  const handleArrow = useGridFocus(tableRef);

  // Prop validation
  if (cells.length !== rows.length) {
    // eslint-disable-next-line no-console
    console.warn(
      `[HeatMap] cells.length (${cells.length}) !== rows.length (${rows.length}). ` +
      `Rendering min(${cells.length}, ${rows.length}) rows.`
    );
  }

  const rowCount = Math.min(rows.length, cells.length);

  const getLabel = (fn, key) => (fn ? fn(key) : key);
  const isSignal = (r, c) =>
    Array.isArray(signalCell) && signalCell[0] === r && signalCell[1] === c;

  return (
    <table
      ref={tableRef}
      role="grid"
      aria-label={ariaLabel}
      className="heatmap"
    >
      <thead>
        <tr>
          {/* Empty corner cell above row-label column */}
          <th className="heatmap-corner" />
          {cols.map((col) => (
            <th
              key={col}
              scope="col"
              className="heatmap-col-header"
            >
              {getLabel(colLabel, col)}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.slice(0, rowCount).map((row, r) => {
          const rowCells = cells[r] || [];
          if (rowCells.length !== cols.length) {
            // eslint-disable-next-line no-console
            console.warn(
              `[HeatMap] cells[${r}].length (${rowCells.length}) !== cols.length (${cols.length}). ` +
              `Rendering min(${rowCells.length}, ${cols.length}) cells for row ${r}.`
            );
          }
          const cellCount = Math.min(cols.length, rowCells.length);

          return (
            <tr key={row}>
              <th
                scope="row"
                className="heatmap-row-label"
              >
                {getLabel(rowLabel, row)}
              </th>
              {cols.slice(0, cellCount).map((col, c) => {
                let score = rowCells[c];
                if (typeof score !== 'number' || score < 0 || score > 1) {
                  // eslint-disable-next-line no-console
                  console.warn(
                    `[HeatMap] cell[${r}][${c}] value ${score} is outside [0, 1]. Clamping.`
                  );
                  score = clamp(Number(score) || 0, 0, 1);
                }

                const signal = isSignal(r, c);
                const label = `${getLabel(rowLabel, row)} ${getLabel(colLabel, col)}: ${score.toFixed(2)}`;
                const bandClass = signal ? '' : scoreBand(score);

                return (
                  <td
                    key={col}
                    role="gridcell"
                    tabIndex={0}
                    data-row={r}
                    data-col={c}
                    aria-label={label}
                    title={label}
                    className={`heatmap-cell ${bandClass}${signal ? ' heatmap-cell--signal' : ''}`}
                    onClick={() => onCellClick && onCellClick(row, col, score)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onCellClick && onCellClick(row, col, score);
                      } else {
                        handleArrow(e);
                      }
                    }}
                  >
                    {score.toFixed(2)}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
